// src/components/staff/components/RehireSearchModal.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, InputAdornment, Box, Stack, Typography,
    Button, IconButton, CircularProgress, Avatar, Chip
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    PersonAdd as RehireIcon,
    EventNote as EventNoteIcon
} from '@mui/icons-material';

const RehireSearchModal = ({ open, onClose, onSelect, tenant }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchOffboarded = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_STAFF_API_URL}/staff/offboarded`,
                { withCredentials: true, headers: { 'X-Tenant': tenant } }
            );
            setResults(res.data?.data || []);
        } catch (err) {
            setError('Failed to load offboarded staff list.');
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        if (open) {
            setQuery('');
            fetchOffboarded();
        }
    }, [open, fetchOffboarded]);

    const filtered = results.filter(s => {
        const q = query.toLowerCase();
        return (
            s.full_name?.toLowerCase().includes(q) ||
            s.employee_number?.toLowerCase().includes(q) ||
            s.staff_code?.toLowerCase().includes(q) ||
            s.nrc_number?.toLowerCase().includes(q)
        );
    });

    const statusColor = (status) =>
        status === 'TERMINATED' ? { bg: '#fef2f2', text: '#ef4444', border: '#fecaca' }
            : { bg: '#fffbeb', text: '#d97706', border: '#fde68a' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>

            {/* Header */}
            <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9', p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ p: 0.8, bgcolor: '#ede9fe', borderRadius: 2, display: 'flex' }}>
                            <RehireIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                                Rehire / Rejoin
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Search resigned or terminated staff to rehire
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            {/* Search */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                <TextField
                    fullWidth autoFocus size="small"
                    placeholder="Search by name, employee ID, NRC..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 2.5, bgcolor: '#f8fafc', '& fieldset': { border: '1.5px solid #e2e8f0' } }
                    }}
                />
            </Box>

            {/* Results */}
            <DialogContent sx={{ p: 0, maxHeight: 420, overflowY: 'auto' }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress size={32} sx={{ color: 'primary.main' }} />
                    </Box>
                )}
                {error && (
                    <Typography sx={{ p: 3, color: '#ef4444', textAlign: 'center' }}>{error}</Typography>
                )}
                {!loading && filtered.length === 0 && (
                    <Typography sx={{ p: 4, color: '#94a3b8', textAlign: 'center', fontWeight: 600 }}>
                        No resigned / terminated staff found.
                    </Typography>
                )}
                {!loading && filtered.map((s) => {
                    const sc = statusColor(s.employment_status);
                    return (
                        <Box
                            key={s.staff_id}
                            onClick={() => onSelect(s)}
                            sx={{
                                px: 3, py: 2, borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 2,
                                '&:hover': { bgcolor: '#f5f3ff' }, transition: 'background 0.15s'
                            }}
                        >
                            <Avatar sx={{ width: 40, height: 40, bgcolor: '#e0e7ff', color: 'primary.dark', fontWeight: 800, fontSize: 14 }}>
                                {s.full_name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                        {s.full_name}
                                    </Typography>
                                    <Chip
                                        label={s.employment_status}
                                        size="small"
                                        sx={{
                                            fontSize: 10, fontWeight: 800, height: 18,
                                            bgcolor: sc.bg, color: sc.text,
                                            border: `1px solid ${sc.border}`
                                        }}
                                    />
                                    {s.employment_sequence > 1 && (
                                        <Chip
                                            label={`Rehire #${s.employment_sequence}`}
                                            size="small"
                                            sx={{ fontSize: 10, fontWeight: 800, height: 18, bgcolor: '#ede9fe', color: '#6d28d9' }}
                                        />
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={2} sx={{ mt: 0.3 }}>
                                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                        {s.employee_number || s.staff_code}
                                    </Typography>
                                    {s.cessation_date && (
                                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                            <EventNoteIcon sx={{ fontSize: 11 }} />
                                            Last day: {new Date(s.cessation_date).toLocaleDateString('en-GB')}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                            <Button
                                size="small" variant="outlined"
                                sx={{
                                    borderColor: 'primary.main', color: 'primary.main', fontWeight: 700,
                                    textTransform: 'none', borderRadius: 2, px: 1.5, fontSize: 12,
                                    '&:hover': { bgcolor: '#ede9fe', borderColor: 'primary.main' }
                                }}
                            >
                                Select
                            </Button>
                        </Box>
                    );
                })}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={onClose} sx={{ fontWeight: 700, color: '#64748b', textTransform: 'none' }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RehireSearchModal;
